import { useEffect, useRef, useState } from "react";
import { observer } from "mobx-react";
import {
  IconAlertTriangle,
  IconCheck,
  IconClock,
  IconInfoCircle,
  IconLoader2,
} from "@tabler/icons-react";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useTranslation } from "@/i18n";
import { cn } from "@/lib/utils";

/** Smallest amount of time the spinner stays visible after a save fires.
 *  Without this floor, a 10ms autosave round-trip flashes the saving
 *  state for a single frame and looks jittery. */
const MIN_SPINNER_MS = 600;

/** How long the green "Saved" checkmark lingers before settling into
 *  the clock + relative-time display. */
const CELEBRATION_MS = 1200;

type Phase = "idle" | "saving" | "celebration" | "pending" | "settled" | "error";

interface AutosaveStatusPillProps {
  isSaving: boolean;
  /** ISO timestamp of the last successful save. Used to render the
   *  "Saved Xs ago" label when settled. */
  lastSavedIso: string | null;
  /** True when the surface has autosaved changes that are not yet
   *  published. Drives the amber ``pending`` phase. Features without a
   *  draft/publish split (e.g. project templates) pass ``false``. */
  hasUnpublishedChanges: boolean;
  /** Human-readable list of fields that differ from the last-published
   *  version, rendered in the hover tooltip on the ``pending`` /
   *  ``settled`` pill. Empty list = no tooltip. */
  changedFields: string[];
  /** True while the surface's last save attempt failed and has not been
   *  superseded by a successful one (``useAutosave``'s ``hasError``).
   *  Renders the persistent red "Save failed" phase — a toast alone
   *  auto-dismisses, and settling back to "Up to date" after a failed
   *  save would misreport the surface's state (NG-334). */
  hasError?: boolean;
  /** Extra classes merged onto the Badge. Use ``min-w-0`` to drop the default
   *  112px width floor where the pill sits next to a heading (so it hugs the
   *  label instead of trailing empty space) rather than in a phase-changing
   *  toolbar where the floor prevents width jitter. */
  className?: string;
  /** Show the pill only while there is something to say: "Saving…", the brief
   *  "Saved", and a persistent "Save failed". At rest (``idle``, ``settled``,
   *  ``pending``) the badge is visually hidden but stays in the accessibility
   *  tree as the same ``role="status"``, so a screen reader can still read "Up
   *  to date" and a spec can still read the settled state. For a panel header
   *  that should be quiet when nothing is happening (NG-2161). */
  hideAtRest?: boolean;
  /** For a surface with no publish step, where "Up to date" cannot tell an
   *  untouched page from a saved edit: once a save has succeeded this session,
   *  settle on "Saved <time>" instead. Before any save it still reads "Up to
   *  date" (NG-2594, project templates). */
  settlesOnSaved?: boolean;
}

function formatRelative(iso: string | null): string {
  if (!iso) return "—";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "—";
  const diff = (Date.now() - date.getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86_400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 86_400 * 7) return `${Math.floor(diff / 86_400)}d ago`;
  return date.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

/** Initial phase on mount — ``pending`` (amber, "Unpublished changes")
 *  surfaces autosaved drafts left behind by a previous session so the
 *  user sees them immediately. */
function deriveInitial(args: {
  isSaving: boolean;
  hasError: boolean;
  hasUnpublishedChanges: boolean;
  lastSavedIso: string | null;
}): Phase {
  if (args.isSaving) return "saving";
  if (args.hasError) return "error";
  if (args.hasUnpublishedChanges) return "pending";
  if (args.lastSavedIso) return "settled";
  return "idle";
}

/** Resting phase after the user has touched the surface this session
 *  (i.e. saving / celebration just played out). ``pending`` is a
 *  load-time signal only — once the user has actively edited, the
 *  pill settles green with the "Saved Xs ago" relative time even
 *  though ``has_unpublished_changes`` is still true. The hover
 *  tooltip continues to surface the pending field list either way. */
function deriveResting(args: { lastSavedIso: string | null }): Phase {
  return args.lastSavedIso ? "settled" : "idle";
}

export const AutosaveStatusPill = observer(function AutosaveStatusPill({
  isSaving,
  lastSavedIso,
  hasUnpublishedChanges,
  changedFields,
  hasError = false,
  className,
  hideAtRest = false,
  settlesOnSaved = false,
}: AutosaveStatusPillProps) {
  const { t } = useTranslation();
  const [phase, setPhase] = useState<Phase>(() =>
    deriveInitial({ isSaving, hasError, hasUnpublishedChanges, lastSavedIso }),
  );
  // Records when ``isSaving`` flipped to true so we can enforce the
  // minimum spinner duration regardless of how fast the PATCH returns.
  const savingStartRef = useRef<number | null>(null);
  // Whether a save succeeded since mount. Set as a save finishes, before the
  // phase change that renders it, so a ref is enough.
  const savedThisSessionRef = useRef(false);
  // Ticks the "Saved Xs ago" label forward without a re-render storm.
  const [, setTickKey] = useState(0);

  useEffect(() => {
    if (isSaving) {
      setPhase("saving");
      savingStartRef.current = Date.now();
      return;
    }

    // A failed save must land red immediately, whatever phase preceded it —
    // no celebration, no min-spinner theatrics, no settling back to a stale
    // green "Up to date" (NG-334). The phase holds until ``hasError`` clears
    // (a later save succeeded) or a new save starts.
    if (hasError) {
      setPhase("error");
      return;
    }

    // ``isSaving`` just flipped to false. If we're not mid-save (e.g. on
    // mount, or after publish / discard cleared the pending state), just
    // derive directly — ``pending`` shows only when the surface was opened
    // with pre-existing unpublished changes the user hasn't touched yet.
    if (phase !== "saving") {
      // ``pending`` (amber) is preserved on mount but never re-entered
      // from a non-saving transition: a publish or discard should always
      // settle green/idle, not amber.
      if (phase === "pending" && hasUnpublishedChanges) return;
      setPhase(deriveResting({ lastSavedIso }));
      return;
    }

    savedThisSessionRef.current = true;
    let cancelled = false;
    let enterRest: ReturnType<typeof setTimeout> | null = null;
    const elapsed = Date.now() - (savingStartRef.current ?? Date.now());
    const remaining = Math.max(0, MIN_SPINNER_MS - elapsed);

    const enterCelebration = setTimeout(() => {
      if (cancelled) return;
      setPhase("celebration");
      enterRest = setTimeout(() => {
        if (cancelled) return;
        // After the green checkmark holds for CELEBRATION_MS, settle
        // green with the "Saved Xs ago" relative time — even though
        // ``has_unpublished_changes`` is still true, the user has just
        // actively edited so the amber ``pending`` state would feel
        // backwards. The tooltip below still surfaces the pending field
        // list whenever there's something to show.
        setPhase(deriveResting({ lastSavedIso }));
      }, CELEBRATION_MS);
    }, remaining);

    return () => {
      cancelled = true;
      clearTimeout(enterCelebration);
      if (enterRest !== null) clearTimeout(enterRest);
    };
    // ``phase`` is read inside the effect but intentionally excluded from
    // deps: we only want to react to ``isSaving`` flips, not phase
    // self-transitions.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSaving, hasError, hasUnpublishedChanges, lastSavedIso]);

  // While settled, tick once a minute so "Xs ago" stays accurate without
  // a per-second re-render. The label still shows seconds for the first
  // minute via ``formatRelative``; this just refreshes "1m ago" → "2m ago".
  useEffect(() => {
    if (phase !== "settled") return;
    const handle = setInterval(() => setTickKey((k) => k + 1), 30_000);
    return () => clearInterval(handle);
  }, [phase]);

  // A settled pill names when it saved while there is unpublished work, or, on
  // a surface with no publish step, once this session has saved something.
  const showsSavedAt = hasUnpublishedChanges || (settlesOnSaved && savedThisSessionRef.current);

  // Map each phase to one of the shared Badge ``status-*`` variants +
  // the right icon. Reusing Badge guarantees the pill is visually
  // identical to the Active status badge sitting next to it (same
  // height, font, padding, system colour tokens), so the toolbar reads
  // as one consistent row of status-pills.
  const styles: Record<
    Phase,
    {
      variant: "status-neutral" | "status-warning" | "status-success" | "destructive";
      iconKey: string;
      icon: React.ReactNode;
      label: string;
    }
  > = {
    // ``size-2.5`` matches the Badge primitive's own ``[&>svg]:size-2.5!``
    // rule — but that selector only targets direct SVG children, and we
    // wrap each icon in a ``<span key=…>`` for the swap-animation, so we
    // have to set the size explicitly here.
    idle: {
      variant: "status-neutral",
      iconKey: "idle",
      icon: <IconClock className="size-2.5" />,
      label: t("autosave.idle", undefined, "Not saved yet"),
    },
    saving: {
      variant: "status-warning",
      iconKey: "saving",
      icon: <IconLoader2 className="size-2.5 animate-spin" />,
      label: t("autosave.saving", undefined, "Saving…"),
    },
    error: {
      // The Badge's ``destructive`` variant follows the same recipe as the
      // ``status-*`` set (tinted bg + colored fg), so the row of pills stays
      // visually consistent while the failure reads unmistakably red.
      variant: "destructive",
      iconKey: "error",
      icon: <IconAlertTriangle className="size-2.5" />,
      label: t("autosave.save_failed_pill", undefined, "Save failed"),
    },
    celebration: {
      variant: "status-success",
      iconKey: "celebration",
      icon: <IconCheck className="size-2.5" />,
      label: t("autosave.saved", undefined, "Saved"),
    },
    pending: {
      variant: "status-warning",
      iconKey: "pending",
      icon: <IconInfoCircle className="size-2.5" />,
      label: t("autosave.pending", undefined, "Unpublished changes"),
    },
    settled: {
      variant: "status-success",
      // ``settled`` has two sub-states keyed off ``showsSavedAt``. With
      // pending work we show "Saved Xs ago" + clock (you just autosaved,
      // here's when). Without it a publish surface is fully caught up to the
      // published version, so "Up to date" reads more honestly than
      // "Saved Xs ago" (which would attach a timestamp to a non-event). A
      // surface with no publish step opts into ``settlesOnSaved``, since
      // there "Up to date" would read the same before and after a save.
      iconKey: showsSavedAt ? "settled-saved" : "settled-uptodate",
      icon: <IconClock className="size-2.5" />,
      label: showsSavedAt
        ? t("autosave.saved_at", {
            time: formatRelative(lastSavedIso),
          })
        : t("autosave.up_to_date", undefined, "Up to date"),
    },
  };

  const current = styles[phase];
  const atRest = phase === "idle" || phase === "settled" || phase === "pending";

  const pill = (
    <Badge
      variant={current.variant}
      role="status"
      aria-live="polite"
      className={cn(
        // ``min-w`` floor keeps the badge from snapping narrower on short
        // labels (e.g. "Saved") and back wider on longer labels (e.g.
        // "Saved 5m ago") — without it the phase transition jitters the
        // toolbar. ``justify-start`` + ``shrink-0`` on the icon keep the
        // icon anchored to the left so it doesn't drift between phases.
        // ``transition-colors`` cross-fades bg + fg as the variant swaps.
        "min-w-[112px] justify-start gap-1 transition-colors duration-(--motion-duration-base)",
        className,
        // Last, so it wins over a caller's layout classes.
        hideAtRest && atRest && "sr-only",
      )}
    >
      {/* ``key`` swap forces a remount of the icon span on every phase
          change. The ``starting:`` variant maps to ``@starting-style``, so a
          freshly-mounted span starts at opacity 0 / scale 0.9 and transitions
          to its resting defaults (opacity 1, scale 1) — replaying the
          fade-in / zoom-in entrance per phase without tw-animate-css. The
          Badge primitive's child-svg rule pins icons to ``size-2.5``, so
          our spinner / checkmark / clock all match the Active badge's
          icon size automatically. */}
      <span
        key={current.iconKey}
        className="inline-flex shrink-0 transition-[opacity,transform] duration-(--motion-duration-base) ease-motion-out starting:scale-90 starting:opacity-0"
      >
        {current.icon}
      </span>
      <span className="flex-1 truncate">{current.label}</span>
    </Badge>
  );

  // Hover tooltip lists the fields that differ from the last-published
  // version. Available whenever there's something to surface — both the
  // ``pending`` (load-time amber) and ``settled`` (post-save green)
  // phases qualify if ``changedFields`` is non-empty. We skip the tooltip
  // during ``saving`` / ``celebration`` (transient) and ``idle`` (nothing
  // to show).
  // At rest a hidden pill is `sr-only`, which is `position: absolute`. With no
  // positioned ancestor its containing block is outside whatever scroll pane
  // renders it, so the 1px box sits at its static position below the fold and
  // stretches the page: on project Settings → General a settled intake save
  // grew a second scrollbar (NG-2414). The wrapper is that positioned
  // ancestor, so the box stays inside its own container whatever the caller's
  // layout. Always rendered with the prop, so the DOM does not change shape
  // between phases. A hidden-at-rest pill also never tooltips: it has nothing
  // on screen to hover.
  if (hideAtRest) return <span className="relative inline-flex">{pill}</span>;
  const tooltipEligible = phase === "pending" || phase === "settled";
  if (!tooltipEligible || changedFields.length === 0) return pill;

  return (
    <TooltipProvider>
      <Tooltip>
        {/* Wrap the pill so the trigger inherits its bounds and the hover
            covers the whole badge, not just the icon. */}
        <TooltipTrigger render={<span tabIndex={0}>{pill}</span>} />
        <TooltipContent
          side="bottom"
          align="end"
          className="max-w-xs flex-col items-start gap-1 px-3 py-2"
        >
          <p className="text-[11px] font-medium tracking-wide text-background/70 uppercase">
            {t("autosave.pending_changes", undefined, "Pending changes")}
          </p>
          <ul className="space-y-0.5 text-xs">
            {changedFields.map((field) => (
              <li key={field}>• {field}</li>
            ))}
          </ul>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
});
